/**
 * Responsive Image Utilities for Project Lighthouse
 * Provides helpers, components, and hooks for optimal image handling
 */

import React, { useState, useEffect, useRef } from 'react';

// Types for responsive image configurations
export interface ResponsiveImageConfig {
  src: string;
  alt: string;
  srcSet?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
  aspectRatio?: string;
  placeholder?: string;
  fallback?: string;
  className?: string;
}

export interface ResponsiveImageSizes {
  mobile: string;
  tablet: string;
  desktop: string;
  xl?: string;
}

// Breakpoints matching our theme
export const BREAKPOINTS = {
  xs: 320,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;

/**
 * Generate srcset string from base URL and sizes
 */
export const generateSrcSet = (
  baseUrl: string,
  sizes: number[],
  format: 'webp' | 'jpg' | 'png' = 'webp'
): string => {
  return sizes
    .map((size) => `${baseUrl}?w=${size}&f=${format} ${size}w`)
    .join(', ');
};

/**
 * Generate sizes attribute for responsive images
 */
export const generateSizes = (config: ResponsiveImageSizes): string => {
  const { mobile, tablet, desktop, xl } = config;
  let sizes = `(max-width: ${BREAKPOINTS.sm}px) ${mobile}`;
  sizes += `, (max-width: ${BREAKPOINTS.lg}px) ${tablet}`;
  if (xl) {
    sizes += `, (max-width: ${BREAKPOINTS.xl}px) ${desktop}`;
    sizes += `, ${xl}`;
  } else {
    sizes += `, ${desktop}`;
  }
  return sizes;
};

/**
 * Hook for lazy loading images with intersection observer
 */
export const useLazyImage = (src: string) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView && !isLoaded) {
      const img = new Image();
      img.onload = () => {
        setIsLoaded(true);
        if (imgRef.current) {
          imgRef.current.setAttribute('data-loaded', 'true');
        }
      };
      img.src = src;
    }
  }, [isInView, isLoaded, src]);

  return { imgRef, isLoaded, isInView, shouldLoad: isInView };
};

/**
 * Hook for responsive image sizing
 */
export const useResponsiveImageSize = () => {
  const [screenSize, setScreenSize] = useState<keyof typeof BREAKPOINTS>('md');

  useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth;
      if (width < BREAKPOINTS.sm) setScreenSize('xs');
      else if (width < BREAKPOINTS.md) setScreenSize('sm');
      else if (width < BREAKPOINTS.lg) setScreenSize('md');
      else if (width < BREAKPOINTS.xl) setScreenSize('lg');
      else setScreenSize('xl');
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return screenSize;
};

/**
 * Responsive Image Component with built-in optimizations
 */
export interface ResponsiveImageProps extends ResponsiveImageConfig {
  width?: number;
  height?: number;
  priority?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  srcSet,
  sizes,
  loading = 'lazy',
  objectFit = 'cover',
  aspectRatio,
  placeholder,
  fallback,
  className = '',
  width,
  height,
  priority = false,
  onLoad,
  onError,
}) => {
  const { imgRef, isLoaded, shouldLoad } = useLazyImage(src);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const imgSrc = priority || shouldLoad ? src : placeholder || '';
  const actualSrc = hasError && fallback ? fallback : imgSrc;

  const imgStyle: React.CSSProperties = {
    objectFit,
    ...(aspectRatio && { aspectRatio }),
    ...(width && { width }),
    ...(height && { height }),
  };

  const imgClasses = [
    className,
    'responsive-img',
    isLoaded ? 'loaded' : 'loading',
    hasError ? 'error' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return React.createElement('img', {
    ref: imgRef,
    src: actualSrc,
    alt: alt,
    srcSet: srcSet,
    sizes: sizes,
    loading: priority ? 'eager' : loading,
    style: imgStyle,
    className: imgClasses,
    onLoad: handleLoad,
    onError: handleError,
    'data-loaded': isLoaded,
  });
};

/**
 * Picture component for art direction responsive images
 */
export interface PictureSource {
  srcSet: string;
  media?: string;
  type?: string;
  sizes?: string;
}

export interface ResponsivePictureProps {
  sources: PictureSource[];
  fallback: ResponsiveImageConfig;
  className?: string;
}

export const ResponsivePicture: React.FC<ResponsivePictureProps> = ({
  sources,
  fallback,
  className,
}) => {
  return React.createElement(
    'picture',
    { className },
    ...sources.map((source, index) =>
      React.createElement('source', {
        key: index,
        srcSet: source.srcSet,
        media: source.media,
        type: source.type,
        sizes: source.sizes,
      })
    ),
    React.createElement(ResponsiveImage, fallback)
  );
};

/**
 * Aspect Ratio Container for images
 */
export interface AspectRatioContainerProps {
  ratio: number; // width / height (e.g., 16/9 = 1.777)
  children: React.ReactNode;
  className?: string;
}

export const AspectRatioContainer: React.FC<AspectRatioContainerProps> = ({
  ratio,
  children,
  className = '',
}) => {
  const paddingBottom = `${(1 / ratio) * 100}%`;

  return React.createElement(
    'div',
    {
      className: `responsive-img-container ${className}`,
      style: { paddingBottom },
    },
    children
  );
};

/**
 * Image with skeleton loading placeholder
 */
export interface ImageWithSkeletonProps extends ResponsiveImageProps {
  skeletonHeight?: number;
  showSkeleton?: boolean;
}

export const ImageWithSkeleton: React.FC<ImageWithSkeletonProps> = ({
  skeletonHeight = 200,
  showSkeleton = true,
  ...imageProps
}) => {
  const { isLoaded } = useLazyImage(imageProps.src);

  if (!isLoaded && showSkeleton) {
    return React.createElement('div', {
      className: 'img-placeholder skeleton',
      style: { minHeight: skeletonHeight },
    });
  }

  return React.createElement(ResponsiveImage, imageProps);
};

/**
 * Utility functions for image optimization
 */
export const imageUtils = {
  /**
   * Convert image to WebP if supported
   */
  getOptimizedFormat: (): 'webp' | 'jpg' => {
    const canvas = document.createElement('canvas');
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0
      ? 'webp'
      : 'jpg';
  },

  /**
   * Calculate optimal image size based on container and DPR
   */
  calculateOptimalSize: (containerWidth: number, maxWidth = 2048): number => {
    const dpr = window.devicePixelRatio || 1;
    const targetWidth = Math.ceil(containerWidth * dpr);
    
    // Find the next power of 2 or common breakpoint
    const commonSizes = [320, 640, 768, 1024, 1280, 1536, 1920, 2048];
    return commonSizes.find(size => size >= targetWidth) || maxWidth;
  },

  /**
   * Preload critical images
   */
  preloadImage: (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = src;
    });
  },

  /**
   * Create responsive image configuration
   */
  createResponsiveConfig: (
    baseUrl: string,
    alt: string,
    sizes: ResponsiveImageSizes
  ): ResponsiveImageConfig => {
    const srcSet = generateSrcSet(baseUrl, [320, 640, 768, 1024, 1280, 1536]);
    const sizesAttr = generateSizes(sizes);

    return {
      src: `${baseUrl}?w=800&f=webp`,
      alt,
      srcSet,
      sizes: sizesAttr,
      loading: 'lazy',
      objectFit: 'cover',
    };
  },
};

// Export common responsive image configurations
export const commonImageConfigs = {
  /**
   * Full-width hero image
   */
  hero: (src: string, alt: string): ResponsiveImageConfig =>
    imageUtils.createResponsiveConfig(src, alt, {
      mobile: '100vw',
      tablet: '100vw',
      desktop: '100vw',
    }),

  /**
   * Card thumbnail image
   */
  thumbnail: (src: string, alt: string): ResponsiveImageConfig =>
    imageUtils.createResponsiveConfig(src, alt, {
      mobile: '150px',
      tablet: '200px',
      desktop: '250px',
    }),

  /**
   * Avatar/profile image
   */
  avatar: (src: string, alt: string): ResponsiveImageConfig =>
    imageUtils.createResponsiveConfig(src, alt, {
      mobile: '60px',
      tablet: '80px',
      desktop: '100px',
    }),

  /**
   * Gallery image
   */
  gallery: (src: string, alt: string): ResponsiveImageConfig =>
    imageUtils.createResponsiveConfig(src, alt, {
      mobile: '50vw',
      tablet: '33vw',
      desktop: '25vw',
    }),
}; 